// Sender-record diagnosis client (PB-2938), over the notification admin surface.
//
//   GET  /api/v1/notifications/mail/domain-status          → last stored answer
//   POST /api/v1/notifications/mail/domain-status/recheck  → resolve now
//
// Under `/api/v1/notifications/` for the reason mail-api.ts records: the auth
// BFF proxy is first-prefix-wins and ends in a catch-all to playbook-be.

import { csrfHeaders } from "../auth/session-api.js";

const BASE = "/api/v1/notifications/mail/domain-status";

/**
 * `not_configured` and `could_not_check` are NOT failures and must not be
 * rendered as one. The first means the administrator has not supplied a DKIM
 * selector, so there is nowhere to look; the second means the lookup did not
 * complete and says nothing about their records.
 */
export type RecordVerdict =
  | "pass"
  | "review"
  | "fail"
  | "not_configured"
  | "could_not_check";

export interface RecordFinding {
  verdict: RecordVerdict;
  /** What happens to their mail, in plain language. Render verbatim. */
  consequence: string;
  /** The published record, for an administrator who wants to see it. */
  value: string | null;
}

export interface DomainFindings {
  spf: RecordFinding;
  dkim: RecordFinding;
  dmarc: RecordFinding;
  dkimSelector: string | null;
}

export interface DomainStatus {
  domain: string;
  verified: boolean;
  findings: DomainFindings | null;
  lastCheckedAt: string | null;
  /** Why this domain is not being checked, when it is not. */
  reason: string | null;
}

export class MailDomainStatusApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = "MailDomainStatusApiError";
  }
}

async function call(path: string, init: RequestInit = {}): Promise<unknown> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...csrfHeaders(),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    throw new MailDomainStatusApiError(
      body?.message ?? body?.code ?? `Request failed (${response.status})`,
      body?.code,
    );
  }
  return response.status === 204 ? null : response.json();
}

/* Keyed by tenant, like `tenantDomainsQueryKey`. A fixed key lets one cache
   entry serve every tenant, and this shell supports a session being replaced in
   place while an admin page stays mounted — so on a tenant change the panel
   would render the PREVIOUS tenant's sending domain, its verdicts and its
   published record values until the refetch settled. The adjacent auth client
   already carried this fence; this one did not. */
export const mailDomainStatusQueryKey = (tenantId: string | null) =>
  ["notifications", "mail", "domain-status", tenantId] as const;

export async function getMailDomainStatus(): Promise<DomainStatus[]> {
  const body = (await call(BASE)) as { domains: DomainStatus[] };
  return body.domains;
}

export async function recheckMailDomain(): Promise<DomainStatus> {
  return (await call(`${BASE}/recheck`, { method: "POST" })) as DomainStatus;
}
